/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
'use strict';

import { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import {
    compareDbs,
    DbCompareSpec,
    DiffChange,
    DiffResult,
    GitCommitEntry,
    gitLog,
    listFiles,
    revertAll,
    revertChange,
} from '../data';
import { StatusContext } from './StatusContext';
import { MetadataContext } from './MetadataContext';
import MergeDiffDialog from './MergeDiffDialog';
import DiffHoverPanel from './DiffHoverPanel';
import DiffChangesTable from './DiffChangesTable';
import { PendingChange } from '../data';
import LoadDialog from './LoadDialog';

function safePrefix(value: any, len: number): string {
    return String(value || '').slice(0, len);
}

function safeDateYmd(value: any): string {
    const txt = String(value || '');
    return txt.length >= 10 ? txt.slice(0, 10) : txt;
}

function describeSpec(spec: DbCompareSpec | null, commits: GitCommitEntry[]): string {
    if (!spec) return 'Not selected';
    if (spec.kind === 'current') return 'Current DB';
    if (spec.kind === 'lastLoaded') return 'Last Loaded DB';
    if (spec.kind === 'git') {
        const commit = commits.find(c => c.hash === spec.hash);
        if (commit) {
            return `git ${commit.shortHash} - ${commit.message}`;
        }
        return `git ${safePrefix(spec.hash, 7)}`;
    }
    return `${spec.cloud ? 'cloud' : 'local'} ${spec.filename}`;
}

type SelectorTarget = 'from' | 'to' | null;

function CompareTab() {
    const statusContext = useContext(StatusContext);
    const metadataContext = useContext<any>(MetadataContext);

    const [fromSpec, setFromSpec] = useState<DbCompareSpec>({ kind: 'lastLoaded' });
    const [toSpec, setToSpec] = useState<DbCompareSpec>({ kind: 'current' });
    const [result, setResult] = useState<DiffResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [reverting, setReverting] = useState<string | null>(null);
    const [revertingAll, setRevertingAll] = useState(false);

    const [diffItem, setDiffItem] = useState<PendingChange | null>(null);
    const [hoverDiff, setHoverDiff] = useState<PendingChange | null>(null);

    const [selectorTarget, setSelectorTarget] = useState<SelectorTarget>(null);
    const [commits, setCommits] = useState<GitCommitEntry[]>([]);
    const [loadingCommits, setLoadingCommits] = useState(false);
    const [cloudPrefix, setCloudPrefix] = useState<'library/dev' | 'public'>('library/dev');
    const [cloudFiles, setCloudFiles] = useState<Array<{ name: string; size: number; modified: number }>>([]);
    const [loadingCloudFiles, setLoadingCloudFiles] = useState(false);
    const [openLoadDialog, setOpenLoadDialog] = useState(false);

    const canRevert = fromSpec.kind === 'lastLoaded' && toSpec.kind === 'current';

    const selectedSpecLabel = useMemo(() => {
        return {
            from: describeSpec(fromSpec, commits),
            to: describeSpec(toSpec, commits),
        };
    }, [fromSpec, toSpec, commits]);

    async function refreshDiff(nextFrom?: DbCompareSpec, nextTo?: DbCompareSpec) {
        const from = nextFrom || fromSpec;
        const to = nextTo || toSpec;
        setLoading(true);
        setLoadError(null);
        try {
            const rc = await compareDbs(from, to);
            setResult(rc);
        } catch (e: any) {
            const msg = e?.message || String(e);
            setLoadError(msg);
            setResult(null);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        refreshDiff();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!selectorTarget) return;
        if (commits.length === 0 && !loadingCommits) {
            setLoadingCommits(true);
            gitLog()
                .then((rows) => setCommits(rows))
                .catch((e) => statusContext.postError(`Failed to load git history: ${e?.message || e}`))
                .finally(() => setLoadingCommits(false));
        }
    }, [selectorTarget]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!selectorTarget) return;
        setLoadingCloudFiles(true);
        listFiles(true, cloudPrefix)
            .then((rows) => setCloudFiles(rows || []))
            .catch((e) => statusContext.postError(`Failed to load ${cloudPrefix} files: ${e?.message || e}`))
            .finally(() => setLoadingCloudFiles(false));
    }, [selectorTarget, cloudPrefix]); // eslint-disable-line react-hooks/exhaustive-deps

    function applySpec(spec: DbCompareSpec) {
        if (selectorTarget === 'from') {
            setFromSpec(spec);
            refreshDiff(spec, toSpec);
        } else if (selectorTarget === 'to') {
            setToSpec(spec);
            refreshDiff(fromSpec, spec);
        }
        setSelectorTarget(null);
    }

    async function handleRevert(c: DiffChange) {
        if (!canRevert) return;
        const key = c.type === 'fingerprint'
            ? (c.fp?.pvkey || c.before?.pvkey || '')
            : (c.category?.key || c.before?.key || '');
        const id = `${c.type}:${key}`;
        setReverting(id);
        try {
            await revertChange(c.type, key, c.op);
            await refreshDiff();
        } catch (e: any) {
            statusContext.postError(`Revert failed: ${e?.message || e}`);
        } finally {
            setReverting(null);
        }
    }

    async function handleRevertAll() {
        if (!canRevert) return;
        setRevertingAll(true);
        try {
            await revertAll();
            await refreshDiff();
            statusContext.postInfo('Reverted all changes against last loaded DB.');
        } catch (e: any) {
            statusContext.postError(`Revert all failed: ${e?.message || e}`);
        } finally {
            setRevertingAll(false);
        }
    }

    const changes = result?.changes || [];
    const fpCount = changes.filter(c => c.type === 'fingerprint').length;
    const catCount = changes.filter(c => c.type === 'category').length;

    const lastLoadedFilename = String(metadataContext?.lastFileOp?.filename || '').trim();
    const lastLoadedCloud = String(metadataContext?.lastFileOp?.cloud || 'false').trim() === 'true';

    return (
        <>
            <div>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <h4 className="mb-1" style={{ color: '#E6EAF2' }}>Compare Databases</h4>
                        <div style={{ color: '#98A2B3', fontSize: '0.8rem' }}>
                            Select From and To sources. Diff updates automatically after each selection.
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={() => refreshDiff()} disabled={loading || revertingAll}>
                            {loading ? <><Spinner animation="border" size="sm" className="me-1" />Refreshing...</> : 'Refresh'}
                        </Button>
                        {canRevert && changes.length > 0 && (
                            <Button variant="outline-danger" size="sm" onClick={handleRevertAll} disabled={revertingAll}>
                                {revertingAll ? <><Spinner animation="border" size="sm" className="me-1" />Reverting...</> : 'Revert All'}
                            </Button>
                        )}
                    </div>
                </div>

                <Row className="g-2 mb-3">
                    <Col md={6}>
                        <div style={{ background: '#171A21', border: '1px solid #2A3140', borderRadius: 7, padding: '12px 14px' }}>
                            <div style={{ color: '#98A2B3', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>From</div>
                            <div style={{ color: '#E6EAF2', fontSize: '0.82rem', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={selectedSpecLabel.from}>
                                {selectedSpecLabel.from}
                            </div>
                            <Button size="sm" variant="outline-primary" className="mt-2" onClick={() => setSelectorTarget('from')}>Change...</Button>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div style={{ background: '#171A21', border: '1px solid #2A3140', borderRadius: 7, padding: '12px 14px' }}>
                            <div style={{ color: '#98A2B3', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>To</div>
                            <div style={{ color: '#E6EAF2', fontSize: '0.82rem', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={selectedSpecLabel.to}>
                                {selectedSpecLabel.to}
                            </div>
                            <Button size="sm" variant="outline-primary" className="mt-2" onClick={() => setSelectorTarget('to')}>Change...</Button>
                        </div>
                    </Col>
                </Row>

                {loadError && <Alert variant="danger">{loadError}</Alert>}

                {loading && (
                    <div style={{ color: '#98A2B3', margin: '8px 0 12px' }}>
                        <Spinner animation="border" size="sm" className="me-2" />Comparing selected databases...
                    </div>
                )}

                {!loading && changes.length === 0 && !loadError && (
                    <div
                        className="mt-2 px-3 py-2 rounded font-monospace"
                        style={{
                            background: '#0d1117',
                            color: '#39ff14',
                            border: '1px solid #39ff1440',
                            fontSize: '0.85rem',
                            letterSpacing: '0.02em',
                        }}
                    >
                        <span style={{ opacity: 0.5 }}>$ </span>diff --stat from to
                        <br />
                        <span style={{ opacity: 0.7 }}>0 files changed</span>
                        <br />
                        <span style={{ color: '#39ff14' }}>No differences found.</span>
                    </div>
                )}

                {changes.length > 0 && (
                    <>
                        <div className="mb-2" style={{ color: '#98A2B3', fontSize: '0.82rem' }}>
                            <Badge bg="secondary" className="me-2">{fpCount} FP</Badge>
                            <Badge bg="info" className="me-2">{catCount} Cat</Badge>
                            <span>{changes.length} total change{changes.length === 1 ? '' : 's'}</span>
                            {!canRevert && <span className="ms-2" style={{ color: '#F59E0B' }}>Read-only compare mode</span>}
                        </div>
                        <DiffChangesTable
                            changes={changes}
                            onOpen={(pending) => { setHoverDiff(null); setDiffItem(pending); }}
                            onHoverChange={setHoverDiff}
                            showRevert={canRevert}
                            revertingKey={reverting}
                            revertingAll={revertingAll}
                            onRevert={handleRevert}
                        />
                    </>
                )}
            </div>

            <Modal show={selectorTarget !== null} onHide={() => setSelectorTarget(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select {selectorTarget === 'from' ? 'From' : 'To'} Database</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="small text-muted mb-2">Quick options</div>
                        <div className="d-flex flex-wrap gap-2">
                            <Button size="sm" variant="outline-secondary" onClick={() => applySpec({ kind: 'current' })}>Current DB</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => applySpec({ kind: 'lastLoaded' })}>Last Loaded DB</Button>
                            {lastLoadedFilename && (
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => applySpec({ kind: 'backupFile', cloud: lastLoadedCloud, filename: lastLoadedFilename })}
                                >
                                    Last Loaded File ({lastLoadedCloud ? 'cloud' : 'local'})
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="mb-3">
                        <div className="small text-muted mb-2">Git history</div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Button size="sm" variant="outline-secondary" onClick={() => applySpec({ kind: 'current' })}>Current (HEAD)</Button>
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                    setLoadingCommits(true);
                                    gitLog()
                                        .then((rows) => setCommits(rows))
                                        .catch((e) => statusContext.postError(`Failed to load git history: ${e?.message || e}`))
                                        .finally(() => setLoadingCommits(false));
                                }}
                            >
                                Refresh commits
                            </Button>
                        </div>
                        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
                            {loadingCommits ? (
                                <div className="p-2 text-muted"><Spinner animation="border" size="sm" className="me-2" />Loading commits...</div>
                            ) : commits.length === 0 ? (
                                <div className="p-2 text-muted">No commits found.</div>
                            ) : (
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>Hash</th>
                                            <th>Date</th>
                                            <th>Message</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {commits.map((c) => (
                                            <tr key={c.hash}>
                                                <td><code>{c.shortHash}</code></td>
                                                <td>{safeDateYmd(c.date)}</td>
                                                <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.message}</td>
                                                <td>
                                                    <Button size="sm" variant="outline-primary" onClick={() => applySpec({ kind: 'git', hash: c.hash })}>Select</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    <div className="mb-3">
                        <div className="small text-muted mb-2">Cloud backups</div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Form.Select
                                size="sm"
                                style={{ width: 180 }}
                                value={cloudPrefix}
                                onChange={(e) => setCloudPrefix(e.target.value as 'library/dev' | 'public')}
                            >
                                <option value="library/dev">library/dev</option>
                                <option value="public">public</option>
                            </Form.Select>
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                    setLoadingCloudFiles(true);
                                    listFiles(true, cloudPrefix)
                                        .then((rows) => setCloudFiles(rows || []))
                                        .catch((e) => statusContext.postError(`Failed to load ${cloudPrefix} files: ${e?.message || e}`))
                                        .finally(() => setLoadingCloudFiles(false));
                                }}
                            >
                                Refresh files
                            </Button>
                        </div>
                        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
                            {loadingCloudFiles ? (
                                <div className="p-2 text-muted"><Spinner animation="border" size="sm" className="me-2" />Loading cloud files...</div>
                            ) : cloudFiles.length === 0 ? (
                                <div className="p-2 text-muted">No files found in {cloudPrefix}.</div>
                            ) : (
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Size</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cloudFiles.map((f) => {
                                            const fullName = `${cloudPrefix}/${f.name}`;
                                            return (
                                                <tr key={fullName}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{fullName}</td>
                                                    <td>{f.size}</td>
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => applySpec({ kind: 'backupFile', cloud: true, filename: fullName })}
                                                        >
                                                            Select
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="small text-muted mb-2">Load dialog source</div>
                        <Button size="sm" variant="outline-primary" onClick={() => setOpenLoadDialog(true)}>Pick with Load Dialog...</Button>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectorTarget(null)}>Close</Button>
                </Modal.Footer>
            </Modal>

            <LoadDialog
                isOpen={openLoadDialog}
                onCancel={() => setOpenLoadDialog(false)}
                onLoad={async (filename, cloud) => {
                    const spec: DbCompareSpec = { kind: 'backupFile', cloud, filename };
                    if (selectorTarget === 'from' || selectorTarget === 'to') {
                        applySpec(spec);
                    }
                    setOpenLoadDialog(false);
                }}
                title="Select Compare Source"
                primaryLabel="Use For Compare"
                cloud={true}
                enableProgress={false}
            />

            <DiffHoverPanel change={hoverDiff} />
            <MergeDiffDialog change={diffItem} onClose={() => setDiffItem(null)} />
        </>
    );
}

export default CompareTab;
